"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import BotMessage from "./BotMessage"
import UserMessage from "./UserMessage"
import OtherQueryInput from "./OtherQueryInput"
import "./ChatContainer.css"
import { getClientDetails, fetchQuestions, submitInquiry, getClientContactNumber } from "../services/api"

const ChatContainer = ({ config, clientId: propsClientId }) => {
  const urlClientId =
    typeof window !== "undefined" ? window.location.pathname.split("/chatbot/")[1] : null
  const clientId = propsClientId || urlClientId

  // ============================================
  // CORE CHAT STATES
  // ============================================
  const [messages, setMessages] = useState([])
  const [showOptions, setShowOptions] = useState(false)
  const [showTypingEffect, setShowTypingEffect] = useState(false)

  // ============================================
  // CLIENT STATES
  // ============================================
  const [clientName, setClientName] = useState("")
  const [isClientLoaded, setIsClientLoaded] = useState(false)
  const [initialMessagesSet, setInitialMessagesSet] = useState(false)

  // ============================================
  // LEADBOT/SALES DYNAMIC FLOW STATES
  // ============================================
  const [salesFlowActive, setSalesFlowActive] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState(null)
  const [questionHistory, setQuestionHistory] = useState([])
  const [isOtherSelected, setIsOtherSelected] = useState(false)
  const [userQuery, setUserQuery] = useState("")
  const [pendingQuery, setPendingQuery] = useState("")
  const [isQueryInputDisabled, setQueryInputDisabled] = useState(false)
  const [isQuerySubmitted, setIsQuerySubmitted] = useState(false)
  const [isDetailsFormVisible, setIsDetailsFormVisible] = useState(false)
  const [isDetailsSubmitted, setIsDetailsSubmitted] = useState(false)
  const [userInfo, setUserInfo] = useState({ name: "", mobile: "", email: "" })
  const [validationErrors, setValidationErrors] = useState({})

  // ============================================
  // CALLBACK & RESTART STATES
  // ============================================
  const [showCallbackMessage, setShowCallbackMessage] = useState(false)
  const [callbackPreference, setCallbackPreference] = useState(null)
  const [callbackRequested, setCallbackRequested] = useState(false)
  const [showRestartPrompt, setShowRestartPrompt] = useState(false)
  const [showPhoneCallAfterNoCallback, setShowPhoneCallAfterNoCallback] = useState(false)
  const [supportPhoneNumber, setSupportPhoneNumber] = useState("")

  // ✅ Track which bot message's options are currently active (only latest clickable)
  const [activeOptionsIndex, setActiveOptionsIndex] = useState(null)

  // ✅ NEW: Track which option groups (bot messages) have already been answered
  const [answeredOptionIndices, setAnsweredOptionIndices] = useState([])

  const messagesEndRef = useRef()

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.value = 800
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.5)
    } catch (error) {
      console.error("Error playing notification sound:", error)
    }
  }, [])

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  const validateName = (name) => {
    if (!name.trim()) return "Name is required"
    if (name.trim().length < 2) return "Name must be at least 2 characters"
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) return "Name can only contain letters and spaces"
    return ""
  }

  const validateMobile = (mobile) => {
    if (!mobile.trim()) return "Mobile number is required"
    if (!/^[6-9]\d{9}$/.test(mobile.trim())) {
      return "Mobile number must start with 6-9 and be exactly 10 digits"
    }
    return ""
  }

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required"
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.trim())) return "Please enter a valid email address"
    return ""
  }

  const safeSetMessage = (newMessage) => {
    const messageWithTimestamp = { ...newMessage, timestamp: Date.now() }
    setMessages((prev) => [...prev, messageWithTimestamp])
  }

  // ✅ Same helper as old chatbot
  const formatURL = (url) => {
    if (url && !url.startsWith("http")) {
      return "https://" + url
    }
    return url
  }

  // ✅ NEW: helper to mark a bot message options group as answered
  const markOptionsAnswered = (index) => {
    setAnsweredOptionIndices((prev) =>
      prev.includes(index) ? prev : [...prev, index],
    )
  }

  // ============================================
  // EFFECTS
  // ============================================

  // Load client details + welcome text
  useEffect(() => {
    const loadClient = async () => {
      try {
        if (!clientId) {
          setMessages([
            {
              type: "bot",
              text: "Welcome to our Support System",
              timestamp: Date.now(),
            },
          ])
          return
        }

        const res = await getClientDetails(clientId)
        const name = res?.data?.ClientName || res?.data?.Name || ""

        if (res?.status && name) {
          setClientName(name)
          setMessages([
            {
              type: "bot",
              text: `Welcome to our ${name} Bot`,
              timestamp: Date.now(),
            },
          ])
        } else {
          setMessages([
            {
              type: "bot",
              text: "Welcome to our Support System",
              timestamp: Date.now(),
            },
          ])
        }
      } catch (err) {
        console.error("Error loading client details in ChatContainer:", err)
        setMessages([
          {
            type: "bot",
            text: "Welcome to our Support System",
            timestamp: Date.now(),
          },
        ])
      } finally {
        setIsClientLoaded(true)
      }
    }

    loadClient()
  }, [clientId])

  // After welcome, start Leadbot flow
  useEffect(() => {
    if (!isClientLoaded || initialMessagesSet) return
    const timer = setTimeout(() => {
      const hasWelcomeMessage = messages.some((msg) => msg.text.includes("Welcome to"))
      if (hasWelcomeMessage && messages.length === 1) {
        setTimeout(() => {
          setSalesFlowActive(true)
          initializeSalesFlow()
          setInitialMessagesSet(true)
        }, 1500)
      }
    }, 1000)
    return () => clearTimeout(timer)
  }, [messages, isClientLoaded, initialMessagesSet])

  // Scroll on updates
  useEffect(() => {
    scrollToBottom()
  }, [messages, showOptions, isDetailsFormVisible])

  // Load support number
  useEffect(() => {
    const loadContact = async () => {
      if (clientId) {
        const number = await getClientContactNumber(clientId)
        if (number) setSupportPhoneNumber(number)
      }
    }
    loadContact()
  }, [clientId])

  // Track which bot message's options are active (latest one only)
  useEffect(() => {
    let lastWithOptions = null
    messages.forEach((m, idx) => {
      if (m.options && m.options.length > 0) {
        lastWithOptions = idx
      }
    })
    if (lastWithOptions !== null) {
      setActiveOptionsIndex(lastWithOptions)
    }
  }, [messages])

  // ============================================
  // SALES / LEADBOT FLOW
  // ============================================

  const initializeSalesFlow = async () => {
    setShowTypingEffect(true)

    try {
      if (!clientId) throw new Error("Client ID is missing")

      const data = await fetchQuestions(clientId)

      if (!data || !data.questions || data.questions.length === 0) {
        throw new Error("No questions found for this client")
      }

      let firstQuestion = data.questions.find(
        (q) => q.question_level === 1 && q.question_parent_level === 0,
      )

      if (!firstQuestion && data.questions.length > 0) {
        firstQuestion = data.questions.reduce((prev, curr) =>
          prev.question_level < curr.question_level ? prev : curr,
        )
      }

      if (!firstQuestion) throw new Error("No valid first question found")

      const firstUrl =
        firstQuestion.question_type !== 1
          ? formatURL(firstQuestion.question_label || firstQuestion.url)
          : null

      setCurrentQuestion(firstQuestion)
      setSalesFlowActive(true)
      setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          text: firstQuestion.question_text,
          options: [...(firstQuestion.children || []), { id: "other", question_text: "Other" }],
          question_type: firstQuestion.question_type,
          url: firstUrl,
          question_label: firstQuestion.question_label,
          timestamp: Date.now(),
        },
      ])
      setShowOptions(true)
    } catch (error) {
      console.error("Error initializing Sales flow:", error)
      safeSetMessage({
        type: "bot",
        text: "Unable to load options right now. Please try again later.",
      })
    } finally {
      setShowTypingEffect(false)
    }
  }

  // CLICK on any option in dynamic (Leadbot) flow
  const handleSalesOptionClick = (selectedOptionLabel, questionIndex) => {
    // ❌ Older levels -> ignore clicks
    if (questionIndex !== activeOptionsIndex) return

    // ❌ If this group already answered, ignore as well
    if (answeredOptionIndices.includes(questionIndex)) return

    playNotificationSound()

    const currentOptions = messages[questionIndex]?.options || []
    const selectedOption = currentOptions.find((opt) => opt.question_text === selectedOptionLabel)

    if (!selectedOption) return

    // ✅ Mark this question's options as answered → they become permanently unclickable
    markOptionsAnswered(questionIndex)

    // "Other" branch
    if (selectedOptionLabel === "Other") {
      safeSetMessage({ type: "user", text: "Other" })
      safeSetMessage({
        type: "bot",
        text: "To better assist you, please describe your query:",
      })
      setIsOtherSelected(true)
      setShowOptions(false)
      return
    }

    // ✅ Any question_type !== 1 with a link → open in new tab
    const isLinkType = selectedOption.question_type && selectedOption.question_type !== 1
    const linkUrl = formatURL(selectedOption.question_label || selectedOption.url)

    if (isLinkType && linkUrl && typeof window !== "undefined") {
      window.open(linkUrl, "_blank", "noopener,noreferrer")
    }

    // Then process journey
    handleSalesQuestionSelection(selectedOption, questionIndex)
  }

  const handleSalesQuestionSelection = (selectedOption, questionIndex) => {
    setShowTypingEffect(true)

    setTimeout(() => {
      const currentMsg = messages[questionIndex]

      // Save question for any future "previous menu" features (if needed)
      if (currentMsg) {
        setQuestionHistory((prev) => [...prev, currentMsg])
      }

      const nextQuestion = currentMsg?.options?.find((q) => q.id === selectedOption.id)

      setMessages((prev) => {
        const newMessages = [...prev]

        // User message
        newMessages.push({
          type: "user",
          text: selectedOption.question_text,
          timestamp: Date.now(),
        })

        const hasChildren =
          nextQuestion && nextQuestion.children && nextQuestion.children.length > 0

        if (hasChildren) {
          // Show next level options
          newMessages.push({
            type: "bot",
            text: nextQuestion.question_text,
            options: [...nextQuestion.children, { id: "other", question_text: "Other" }],
            question_type: nextQuestion.question_type || currentMsg?.question_type,
            url: null,
            question_label: nextQuestion.question_label || currentMsg?.question_label,
            timestamp: Date.now(),
          })
        } else {
          // Leaf node → show final bot text
          newMessages.push({
            type: "bot",
            text: nextQuestion?.question_text || selectedOption.question_text,
            question_type: nextQuestion?.question_type || currentMsg?.question_type,
            url: null,
            question_label: nextQuestion?.question_label || currentMsg?.question_label,
            timestamp: Date.now(),
          })
        }

        return newMessages
      })

      const hasChildren =
        nextQuestion && nextQuestion.children && nextQuestion.children.length > 0
      const isLeaf = !hasChildren

      setCurrentQuestion(nextQuestion || null)

      // Leaf node -> show callback prompt
      if (isLeaf && !callbackRequested) {
        setCallbackRequested(true)
        setShowCallbackMessage(true)
      }

      setShowTypingEffect(false)
    }, 600)
  }

  // ============================================
  // CALLBACK & DETAILS
  // ============================================

  const handleCallbackSelection = (preference) => {
    setShowTypingEffect(true)
    playNotificationSound()

    setTimeout(() => {
      setCallbackPreference(preference)
      setShowCallbackMessage(false)
      safeSetMessage({ type: "user", text: preference })

      const botMessage =
        preference === "Yes"
          ? "Our representatives will reach out to you shortly."
          : "Alright! Let us know if you need any help later."

      safeSetMessage({ type: "bot", text: botMessage })

      if (preference === "Yes") {
        setTimeout(() => {
          setIsDetailsFormVisible(true)
        }, 600)
      } else {
        setShowPhoneCallAfterNoCallback(true)
        setShowRestartPrompt(true)
      }
      setShowTypingEffect(false)
    }, 600)
  }

  const handleUserDetailsSubmit = async (details) => {
    setShowTypingEffect(true)

    if (!details || !details.name || !details.mobile || !details.email) {
      console.error("Invalid user details:", details)
      setShowTypingEffect(false)
      return
    }

    const nameError = validateName(details.name)
    const mobileError = validateMobile(details.mobile)
    const emailError = validateEmail(details.email)

    if (nameError || mobileError || emailError) {
      setValidationErrors({ name: nameError, mobile: mobileError, email: emailError })
      setShowTypingEffect(false)
      return
    }

    try {
      const inquiryData = {
        Client_name: details.name,
        contact: details.mobile,
        email: details.email,
        last_question: currentQuestion?.id || 0,
        agent_remarks: "",
        Next_followup: "",
        Client_id: clientId,
        other_query: userQuery || pendingQuery,
      }

      await submitInquiry(inquiryData)

      const newMessages = [
        { type: "user", text: `Name: ${details.name}`, timestamp: Date.now() },
        { type: "user", text: `Mobile: ${details.mobile}`, timestamp: Date.now() },
        { type: "user", text: `Email: ${details.email}`, timestamp: Date.now() },
        {
          type: "bot",
          text: "Thank you for sharing your details! Our representatives will get in touch with you shortly.",
          timestamp: Date.now(),
        },
      ]

      setMessages((prev) => [...prev, ...newMessages])
      setIsDetailsSubmitted(true)
      setIsDetailsFormVisible(false)

      setTimeout(() => {
        safeSetMessage({
          type: "bot",
          text: `Please check your registered email for details. Thank you for visiting ${
            clientName || "us"
          }. Our working hours are: Monday to Friday, 10:00 AM to 5:00 PM. Our Admin will contact you.`,
        })
        setShowRestartPrompt(true)
      }, 600)
    } catch (error) {
      console.error("Failed to submit inquiry:", error)
      safeSetMessage({
        type: "bot",
        text: "Failed to submit your inquiry. Please try again later.",
      })
      setTimeout(() => {
        safeSetMessage({
          type: "bot",
          text: "Would you like to try again?",
          options: ["Yes", "No"],
          timestamp: Date.now(),
        })
        setShowOptions(true)
      }, 600)
    }
    setShowTypingEffect(false)
  }

  const handleUserQuerySubmit = (query) => {
    setShowTypingEffect(true)
    playNotificationSound()

    if (!query.trim()) {
      setShowTypingEffect(false)
      return
    }

    setTimeout(() => {
      setUserQuery(query)
      safeSetMessage({ type: "user", text: query })
      safeSetMessage({
        type: "bot",
        text: "Thank you! Now, please provide your contact details so we can reach you.",
      })
      setIsDetailsFormVisible(true)
      setShowTypingEffect(false)
    }, 400)
  }

  // ============================================
  // RESTART
  // ============================================

  const handleRestartConversation = () => {
    const welcomeMessage = clientName
      ? `Welcome to our ${clientName} Bot`
      : "Welcome to our Support System"

    setMessages([{ type: "bot", text: welcomeMessage, timestamp: Date.now() }])

    setShowOptions(false)
    setSalesFlowActive(false)
    setCurrentQuestion(null)
    setQuestionHistory([])
    setIsDetailsFormVisible(false)
    setIsDetailsSubmitted(false)
    setIsOtherSelected(false)
    setShowCallbackMessage(false)
    setCallbackPreference(null)
    setCallbackRequested(false)
    setUserInfo({ name: "", mobile: "", email: "" })
    setValidationErrors({})
    setIsQuerySubmitted(false)
    setUserQuery("")
    setShowRestartPrompt(false)
    setShowPhoneCallAfterNoCallback(false)
    setInitialMessagesSet(false)
    setActiveOptionsIndex(null)
    setAnsweredOptionIndices([]) // ✅ Reset answered groups on restart

    setTimeout(() => {
      setSalesFlowActive(true)
      initializeSalesFlow()
    }, 400)
  }

  const handleRestartResponse = (response) => {
    playNotificationSound()
    if (response === "Yes") {
      handleRestartConversation()
    } else {
      safeSetMessage({ type: "bot", text: "Thank you for connecting with us. Have a great day!" })
    }
    setShowRestartPrompt(false)
  }

  // ============================================
  // RENDER
  // ============================================

  if (!isClientLoaded) return <div className="loading-container">Loading...</div>

  return (
    <div className="chat-container">
      <div className="chat-header">
        <strong>{clientName ? `${clientName} Bot` : "Support Bot"}</strong>
      </div>

      <div className="chat-body">
        {messages.map((msg, idx) => {
          if (msg.type === "bot") {
            return (
              <div key={`${idx}-${msg.timestamp}`} className="message-group">
                <BotMessage text={msg.text} />

                {msg.options && msg.options.length > 0 && showOptions && (
                  <div className="options-container">
                    {msg.options.map((opt, i) => {
                      // options with object + id → Leadbot nodes
                      const isLeadbotOptions = msg.options.some(
                        (o) => typeof o === "object" && o.id,
                      )

                      const alreadyAnswered = answeredOptionIndices.includes(idx)

                      const disabled =
                        (isLeadbotOptions && idx !== activeOptionsIndex) ||
                        alreadyAnswered // ✅ once answered, permanently disabled

                      return (
                        <button
                          key={i}
                          className={`option-button ${disabled ? "option-button-disabled" : ""}`}
                          disabled={disabled}
                          onClick={() => {
                            if (disabled) return
                            if (salesFlowActive && isLeadbotOptions) {
                              handleSalesOptionClick(opt.question_text || opt, idx)
                            } else {
                              if (opt === "Yes" || opt === "No") {
                                handleRestartResponse(opt === "Yes" ? "Yes" : "No")
                              }
                            }
                          }}
                        >
                          {opt.question_text || opt}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          } else if (msg.type === "user") {
            return <UserMessage key={`${idx}-${msg.timestamp}`} text={msg.text} />
          }
          return null
        })}

        {/* "Other" free-text query box */}
        {isOtherSelected && !isDetailsFormVisible && !isDetailsSubmitted && (
          <OtherQueryInput
            handleQuerySubmit={handleUserQuerySubmit}
            isQueryInputDisabled={isQueryInputDisabled}
          />
        )}

        {/* User Details Form */}
        {isDetailsFormVisible && !isDetailsSubmitted && (
          <div className="user-form-container">
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                placeholder="Enter your full name"
                value={userInfo.name}
                onChange={(e) => {
                  setUserInfo((prev) => ({ ...prev, name: e.target.value }))
                  if (validationErrors.name)
                    setValidationErrors((prev) => ({ ...prev, name: "" }))
                }}
                style={{ textAlign: "left" }}
              />
            </div>
            {validationErrors.name && <div className="error-message">{validationErrors.name}</div>}

            <div className="form-group">
              <label>Mobile Number *</label>
              <input
                type="text"
                placeholder="10-digit mobile"
                value={userInfo.mobile}
                maxLength="10"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "")
                  if (val.length <= 10) {
                    setUserInfo((prev) => ({ ...prev, mobile: val }))
                    if (validationErrors.mobile)
                      setValidationErrors((prev) => ({ ...prev, mobile: "" }))
                  }
                }}
                style={{ textAlign: "left" }}
              />
            </div>
            {validationErrors.mobile && (
              <div className="error-message">{validationErrors.mobile}</div>
            )}

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                placeholder="Enter your email"
                value={userInfo.email}
                onChange={(e) => {
                  setUserInfo((prev) => ({ ...prev, email: e.target.value }))
                  if (validationErrors.email)
                    setValidationErrors((prev) => ({ ...prev, email: "" }))
                }}
                style={{ textAlign: "left" }}
              />
            </div>
            {validationErrors.email && (
              <div className="error-message">{validationErrors.email}</div>
            )}

            <button className="form-submit-button" onClick={() => handleUserDetailsSubmit(userInfo)}>
              Submit Details
            </button>
          </div>
        )}

        {/* Callback prompt */}
        {showCallbackMessage && (
          <div className="callback-container">
            <p>Request a Callback?</p>
            <div className="d-flex">
              <button className="btn btn-primary" onClick={() => handleCallbackSelection("Yes")}>
                Yes
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => handleCallbackSelection("No")}
              >
                No
              </button>
            </div>
          </div>
        )}

        {/* Restart prompt + phone CTA */}
        {showRestartPrompt && (
          <div>
            <div className="alert alert-info text-center p-2 mt-2">
              <p className="mb-2">Would you like to start again?</p>
              <div className="d-flex justify-content-center gap-2">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleRestartResponse("Yes")}
                >
                  Yes
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handleRestartResponse("No")}
                >
                  No
                </button>
              </div>
            </div>
            {showPhoneCallAfterNoCallback && supportPhoneNumber && (
              <div className="text-center my-3">
                <a
                  href={`tel:${supportPhoneNumber}`}
                  className="btn btn-success d-inline-flex align-items-center"
                >
                  <i className="bi bi-telephone me-2"></i> For Quick Support Call to{" "}
                  {supportPhoneNumber}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Typing Effect */}
        {showTypingEffect && (
          <div className="typing-effect">
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-footer">⚡ Powered by ATai Support</div>
    </div>
  )
}

export default ChatContainer
